import {
  ETextAlign,
  PdfBrush,
  PdfColor,
  PdfFont,
  PdfGen,
  PdfRect,
  PdfSize,
  PdfUnit,
} from '../../pdf/pdf';
import { ILayoutSingleGen } from '../layoutSingle';
import { OptsSingle } from '../optsSingle';
import { GUtil } from '../../pdf/gutil';
import { generateMaze, findSolution } from '../../core/maze';
import { PoolDict } from '../../core/poolDict';
import { C } from '../../core/c';
import { fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import { QSingle } from '@educraft/base';

const YELLOW = new PdfBrush(PdfColor.fromRgb(255, 255, 0));
const POOL_SIZE = 500;

/**
 * Bludiste__Base (Maze - Base) - Math maze worksheet generator.
 *
 * Layout: Portrait pages with increasing maze difficulty.
 */
export class BludisteSpoj implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const rnd = opts.data.random;
    const totalPages = 6;

    // Build pool: bucket by answer, deduplicate/display by joined question text
    const pool = new PoolDict<QSingle>(
      (q) => q.answer,
      (q) => q.texts.join(' ')
    );
    for (let i = 0; i < POOL_SIZE; i++) {
      const generated = opts.generator.generate(opts.data);
      pool.add(generated);
    }
    const { good: goodPool, bad: badPool }: { good: QSingle[]; bad: QSingle[] } =
      pool.getRandom(rnd);

    // Display = question text only, strip trailing '='
    const toDisplay = (item: QSingle) =>
      fixGenArray(item.texts)
        .join(' ')
        .replace(/[= ]+$/, '')
        .trim();
    const allDisplayTexts = [...goodPool, ...badPool].map(toDisplay);

    for (let p = 0; p < totalPages; p++) {
      const mazeSizeX = 7 + p * 2;
      const mazeSizeY = 9 + p * 2;

      const size = (PdfSize.A4.width - 2 * margin) / (mazeSizeX - 2);
      const topHeight = (PdfSize.A4.height - 2 * margin - size * (mazeSizeY - 2)) / 2;
      const originX = margin;
      const originY = margin + topHeight;

      const startX = Math.floor(mazeSizeX / 2) | 1;
      const startY = 1;
      const endX = startX;
      const endY = (mazeSizeY - 2) | 1;

      let solutionSet!: Set<string>;

      while (true) {
        const maze = generateMaze(rnd, mazeSizeX, mazeSizeY);
        const solution = findSolution(maze, new C(startX, startY), new C(endX, endY));
        if (solution.length === 0) continue;
        solutionSet = new Set(solution.map((c: C) => c.toHash()));
        break;
      }

      const page = pdf.addPage();

      const padding = PdfUnit.FromMillimeters(1);

      const font =
        allDisplayTexts.length > 0
          ? page.adjustFont(size - padding, allDisplayTexts, GUtil.baseFont)
          : new PdfFont(PdfFont.FONT_NAME, size * 0.5);

      for (let x = 1; x < mazeSizeX - 1; x++) {
        for (let y = 1; y < mazeSizeY - 1; y++) {
          const r = new PdfRect(originX + (x - 1) * size, originY + (y - 1) * size, size, size);

          const isEndpoint = (x === startX && y === startY) || (x === endX && y === endY);
          if (isEndpoint) {
            page.drawRectangle(null, YELLOW, r);
          }

          page.drawRectangle(GUtil.basePen, null, r);

          const items = solutionSet.has(`${x},${y}`) ? goodPool : badPool;
          if (items.length === 0) continue;

          const item = items[rnd.next(0, items.length)];
          page.drawStrings(
            fixGenArray(item.texts),
            font,
            PdfBrush.Black,
            null,
            r,
            ETextAlign.Center
          );
        }
      }
    }
  }
}
